/**
 * Continuous (Dauertermin) calendar controller — DATA ONLY.
 *
 * The first controller born under the split architecture: one lean hook per
 * view mode on top of the SHARED core (query hooks, adapters, validations),
 * instead of more `continuousView` branches inside the 2000-line legacy
 * CalendarController. It produces exactly what the continuous view-model
 * builder needs and nothing else:
 *
 *   resources        therapist columns with continuous work-plan hours
 *   calendarEvents   weekly-template events (adapter `daysOfWeek` semantics)
 *   patientAvailabilities  weekly blocks for the blocked band
 *   users / isLoading
 *
 * Deliberately NO mutation handlers here: moves are dispatched by the bridge
 * through the continuous-aware mutations (`move.event.isContinuous` picks the
 * verb), and external drops go through the legacy controller's battle-tested
 * onEventReceive, which is already continuous-capable. Duplicating that flow
 * would fork its optimistic-cache behavior.
 *
 * Query overlap with the legacy controller is free: both address the same
 * react-query keys, so whichever consumer mounts first fills the cache and
 * the other reads it — no duplicate requests.
 *
 * All queries take `enabled` so the hook is inert while the standard view is
 * active (hooks must be called unconditionally; data must not be fetched
 * unconditionally).
 */
import { useMemo } from "react";
import {
  AppointmentDto,
  ContinuousAppointmentDto,
  ContinuousEventTimeSlotDto,
  PatientAvailabilityDto,
  UserDto,
} from "../../../api-client";
import useStore from "../../../helpers/useStore";
import { useLocationId } from "../../../stores/User/User.hooks";
import {
  useContinuousAppointmentsByDayAndLocation,
  useContinuousAppointmentsForUser,
  usePatientUnavailabilityForCalendar,
  useSublocalitiesForAppointmentsAddresses,
} from "../../../stores/Calendar/calendar.queries";
import { CalendarContext } from "../../../stores/Calendar/calendar.provider";
import useCalendarValidations from "../../Calendar/Calendar.validations";
import { toCalendarEvents, toCalendarResources } from "../../Calendar/controller/CalendarAdapters";
import { mergeGroupPatientIntoContinuousTherapistAppointments } from "../../Calendar/controller/shared/continuousGroupMerge";
import { useSelectedUsers } from "../../Calendar/controller/useSelectedUsers";
import {
  CalendarEvent,
  CalendarResource,
} from "../../Calendar/controller/adapters/types";

export interface ContinuousCalendarData {
  resources: CalendarResource[];
  calendarEvents: CalendarEvent[];
  users: UserDto[];
  patientAvailabilities: PatientAvailabilityDto[] | undefined;
  selectedDate: Date;
  isLoading: boolean;
}

export const useContinuousCalendarController = (enabled: boolean): ContinuousCalendarData => {
  const CalendarStore = useStore(CalendarContext);
  const { selectedDateStr, selectedPatientId, columnUserIds, groupCheck, isRoleMode } = CalendarStore;
  const locationId = useLocationId();
  const selectedDate = useMemo(() => new Date(selectedDateStr), [selectedDateStr]);

  const { fetchedUsers, continuousWorkPlansForUsers } = useSelectedUsers(
    locationId,
    CalendarStore,
  );
  const { isTimeOutsideContinuousWorkingHours } = useCalendarValidations();

  const { data: cachedByDay, isLoading: byDayLoading } =
    useContinuousAppointmentsByDayAndLocation(
      locationId!,
      selectedDate.getDay(),
      enabled && !!locationId,
    );

  // Single-column mode prefers the user-specific cache (it spans all
  // weekdays), mirroring the legacy controller's preference order.
  const selectedUserId =
    columnUserIds && columnUserIds.length === 1 ? columnUserIds[0] : undefined;
  const { data: cachedForUser } = useContinuousAppointmentsForUser(
    selectedUserId,
    enabled && !!selectedUserId,
  );

  const { data: patientAvailabilities } = usePatientUnavailabilityForCalendar(
    selectedPatientId,
    selectedDateStr,
    enabled && !!selectedPatientId,
  );

  const rawAppointments: ContinuousAppointmentDto[] =
    (selectedUserId && cachedForUser?.appointments?.length
      ? cachedForUser.appointments
      : cachedByDay?.appointments) ?? [];
  const eventTimeSlots: ContinuousEventTimeSlotDto[] =
    (selectedUserId && cachedForUser?.eventTimeSlots?.length
      ? cachedForUser.eventTimeSlots
      : cachedByDay?.eventTimeSlots) ?? [];

  const appointments = useMemo(
    () => mergeGroupPatientIntoContinuousTherapistAppointments(rawAppointments),
    [rawAppointments],
  );

  // The hook signature says AppointmentDto[], but it only reads addresses —
  // continuous appointments carry the same shape there. The legacy controller
  // feeds it the identical value.
  const { data: sublocalities = [] } = useSublocalitiesForAppointmentsAddresses(
    appointments as unknown as AppointmentDto[],
    enabled && !!locationId,
  );

  const resources = useMemo(() => {
    if (!enabled || fetchedUsers.length === 0) return [];
    // Empty + role mode means the role-by-date query resolved to nobody for this
    // date, not "show everyone" — see the same guard in CalendarController.
    const filteredUsers =
      columnUserIds && columnUserIds.length > 0
        ? fetchedUsers.filter((user) => columnUserIds.includes(user.id!))
        : isRoleMode
          ? []
          : fetchedUsers;

    // Warning counts, Dauertermin flavor: continuous slots measured against
    // work-plan hours with TIME-ONLY comparison (plans are projected onto the
    // horizon week, so their dates never match the displayed date). Only
    // entries on the displayed weekday count — the user-specific cache spans
    // all weekdays.
    const selectedDayOfWeek = selectedDate.getDay();
    const warningCountMap = new Map<string, number>();
    for (const user of filteredUsers) {
      const userId = user.id!;
      const userWorkingHours = (continuousWorkPlansForUsers ?? []).filter(
        (wh) => wh.userId === userId && !wh.isBreak,
      );
      let warningCount = 0;
      for (const appointment of appointments) {
        const hasThisUser = appointment.timeSlots?.some((ts) => ts.user?.id === userId);
        if (
          hasThisUser &&
          appointment.start &&
          appointment.end &&
          new Date(appointment.start).getDay() === selectedDayOfWeek &&
          isTimeOutsideContinuousWorkingHours(
            new Date(appointment.start),
            new Date(appointment.end),
            userWorkingHours,
          )
        ) {
          warningCount++;
        }
      }
      for (const event of eventTimeSlots) {
        if (
          event.user?.id === userId &&
          event.start &&
          event.end &&
          new Date(event.start).getDay() === selectedDayOfWeek &&
          isTimeOutsideContinuousWorkingHours(
            new Date(event.start),
            new Date(event.end),
            userWorkingHours,
          )
        ) {
          warningCount++;
        }
      }
      if (warningCount > 0) warningCountMap.set(userId, warningCount);
    }

    // NO patientAvailabilities passed: the synthetic patient column must never
    // exist for the MobiliCalendar surface — availability renders as the
    // day-spanning band (viewmodel/continuousDay.ts).
    return toCalendarResources(filteredUsers, {
      selectedDate,
      warningCountMap,
      continuousWorkPlansForUsers,
      isContinuous: true,
    });
  }, [
    enabled,
    fetchedUsers,
    columnUserIds,
    isRoleMode,
    selectedDate,
    continuousWorkPlansForUsers,
    appointments,
    eventTimeSlots,
    isTimeOutsideContinuousWorkingHours,
  ]);

  const calendarEvents = useMemo(() => {
    if (!enabled) return [];
    const resourceIds = new Set(resources.map((r) => r.id));
    const continuousLunchBreaks = (continuousWorkPlansForUsers ?? []).filter(
      (wh) => wh.isBreak && resourceIds.has(wh.userId!),
    );
    return toCalendarEvents({
      appointments: [],
      leaveOfAbsences: [],
      eventTimeslots: [],
      continuousAppointments: appointments,
      continuousEventTimeSlots: eventTimeSlots,
      lunchBreaks: [],
      publicHolidayWorkingHours: [],
      continuousLunchBreaks,
      cancelledDays: [],
      travelTimes: [],
      patientBlockingSlots: [],
      groupCheck,
      lockedAppointments: [],
      isContinuous: true,
      sublocalities,
    });
  }, [
    enabled,
    resources,
    continuousWorkPlansForUsers,
    appointments,
    eventTimeSlots,
    groupCheck,
    sublocalities,
  ]);

  return {
    resources,
    calendarEvents,
    users: fetchedUsers,
    patientAvailabilities,
    selectedDate,
    isLoading: enabled && byDayLoading,
  };
};

export default useContinuousCalendarController;
